import { Injectable, inject } from '@angular/core';
import { GooglePlaceDetailsDto } from '../models/google-maps-link-resolve.model';
import { GoogleOpeningHoursDto } from '../shared/location/google-opening-hours.types';
import { phoneFromPlaceDetails } from '../shared/utils/contact.utils';
import { GoogleMapsLoaderService } from './google-maps-loader.service';

/**
 * Resolves place details in the browser via Maps JavaScript API.
 *
 * Migrated to **Places API (New)** — uses `google.maps.places.Place` instead
 * of the deprecated `PlacesService`. The `PlacesService` (and the legacy
 * `Autocomplete` widget) are blocked for GCP projects created after March
 * 1st, 2025 with `ApiTargetBlockedMapError`. The new API also returns
 * Promise-based results (no callbacks).
 *
 * Output DTO keeps the legacy shape (`place_id`, `long_name`, etc.) so the
 * shared parsing layer in `address-resolution.utils` doesn't need to change.
 */

/** Fields requested from the new Place API. */
const PLACE_FIELDS = [
  'id',
  'displayName',
  'formattedAddress',
  'addressComponents',
  'location',
  'internationalPhoneNumber',
  'nationalPhoneNumber',
  'websiteURI',
  'regularOpeningHours',
] as const;

@Injectable({ providedIn: 'root' })
export class GoogleMapsPlaceClientService {
  private readonly mapsLoader = inject(GoogleMapsLoaderService);

  async resolvePlaceId(placeId: string): Promise<GooglePlaceDetailsDto> {
    await this.#ensureLoaded();
    const place = new google.maps.places.Place({ id: placeId });
    await place.fetchFields({ fields: [...PLACE_FIELDS] });
    return this.#placeToDto(place);
  }

  async resolvePlaceNameNear(
    placeName: string,
    lat: number,
    lng: number,
  ): Promise<GooglePlaceDetailsDto> {
    await this.#ensureLoaded();

    // Text search first — biased to the lat/lng. This is the new-API
    // equivalent of the old `findPlaceFromQuery` + `locationBias` combo.
    try {
      const { places } = await google.maps.places.Place.searchByText({
        textQuery: placeName,
        fields: [...PLACE_FIELDS],
        locationBias: new google.maps.Circle({
          center: { lat, lng },
          radius: 150,
        }),
        maxResultCount: 1,
      });
      if (places[0]) return this.#placeToDto(places[0]);
    } catch (err) {
      console.warn('[PlaceClient] searchByText failed, trying nearby:', err);
    }

    // Nearby search fallback when text search returns nothing.
    try {
      const { places } = await google.maps.places.Place.searchNearby({
        locationRestriction: new google.maps.Circle({
          center: { lat, lng },
          radius: 120,
        }),
        fields: [...PLACE_FIELDS],
        maxResultCount: 1,
      });
      if (places[0]) return this.#placeToDto(places[0]);
    } catch (err) {
      console.warn('[PlaceClient] searchNearby failed, falling back to geocoder:', err);
    }

    return this.resolveLatLng(lat, lng);
  }

  async resolvePlaceByTextQuery(query: string): Promise<GooglePlaceDetailsDto> {
    await this.#ensureLoaded();
    const { places } = await google.maps.places.Place.searchByText({
      textQuery: query.trim(),
      fields: [...PLACE_FIELDS],
      maxResultCount: 1,
    });
    const hit = places[0];
    if (!hit) throw new Error('Find Place (ZERO_RESULTS)');
    return this.#placeToDto(hit);
  }

  /**
   * Geocoder remains on the legacy Geocoding API (separate from Places).
   * No migration needed — the Geocoder class is still the only option in
   * the JS library for reverse-geocoding by lat/lng.
   */
  async resolveLatLng(lat: number, lng: number): Promise<GooglePlaceDetailsDto> {
    await this.#ensureLoaded();

    return new Promise((resolve, reject) => {
      const geocoder = new google.maps.Geocoder();
      geocoder.geocode({ location: { lat, lng } }, (results, status) => {
        if (status !== google.maps.GeocoderStatus.OK || !results?.[0]) {
          reject(new Error(`Geocoder (${status})`));
          return;
        }

        const hit = results[0];
        const placeId = hit.place_id;
        if (placeId) {
          this.resolvePlaceId(placeId).then(resolve).catch(reject);
          return;
        }

        const components = hit.address_components ?? [];
        if (!components.length) {
          reject(new Error('Geocoder sin componentes de dirección.'));
          return;
        }

        const loc = hit.geometry?.location;
        resolve({
          place_id: '',
          name: undefined,
          formatted_address: hit.formatted_address,
          address_components: components.map(c => ({
            long_name: c.long_name,
            short_name: c.short_name,
            types: [...c.types],
          })),
          geometry: loc
            ? { location: { lat: loc.lat(), lng: loc.lng() } }
            : { location: { lat, lng } },
        });
      });
    });
  }

  async #ensureLoaded(): Promise<void> {
    if (!this.mapsLoader.isConfigured()) {
      throw new Error('Google Maps API key no configurada en el frontend.');
    }
    await this.mapsLoader.load();
  }

  /** Maps the new opening-hours model to the legacy DTO shape. */
  #openingHoursDto(hours?: google.maps.places.OpeningHours | null): GoogleOpeningHoursDto | undefined {
    const periods = hours?.periods;
    if (!periods?.length) return undefined;
    return {
      periods: periods.map(p => ({
        open:  { day: p.open.day, time: this.#formatTime(p.open) },
        ...(p.close ? { close: { day: p.close.day, time: this.#formatTime(p.close) } } : {}),
      })),
      weekday_text: hours?.weekdayDescriptions,
    };
  }

  /** New API exposes `hour` + `minute` integers; legacy DTO used "HHMM" string. */
  #formatTime(point: { hour: number; minute: number }): string {
    const hh = String(point.hour).padStart(2, '0');
    const mm = String(point.minute).padStart(2, '0');
    return `${hh}${mm}`;
  }

  /**
   * Bridges the new `Place` shape onto the legacy `GooglePlaceDetailsDto`
   * used everywhere downstream. Keeps changes contained to this file.
   */
  #placeToDto(place: google.maps.places.Place): GooglePlaceDetailsDto {
    const loc = place.location;
    const nationalPhone        = place.nationalPhoneNumber ?? undefined;
    const internationalPhone   = place.internationalPhoneNumber ?? undefined;
    const phone = phoneFromPlaceDetails({
      international_phone_number: internationalPhone,
      formatted_phone_number: nationalPhone,
    });
    return {
      place_id: place.id ?? '',
      name: place.displayName ?? undefined,
      formatted_address: place.formattedAddress ?? undefined,
      formatted_phone_number: nationalPhone,
      international_phone_number: internationalPhone ?? phone,
      website: place.websiteURI ?? undefined,
      opening_hours: this.#openingHoursDto(place.regularOpeningHours),
      address_components: (place.addressComponents ?? []).map(c => ({
        long_name: c.longText ?? '',
        short_name: c.shortText ?? '',
        types: [...c.types],
      })),
      geometry: loc
        ? { location: { lat: loc.lat(), lng: loc.lng() } }
        : undefined,
    };
  }
}
